// @flow
import { SHAPES } from '../../server/shared/constants/zoneConstants';
import { CALL_API } from '../middlewares/api';
import type { Dispatch, GetState, ZoneData, Zone, ShapeType, Shape } from '../types';
import * as constants from '../constants/zoneConstants';
import { drawingShapeTypeSelector, drawingZoneSelector } from '../selectors/zoneSelectors';
import mapService from '../services/map';

type LoadZonesAction = {|
    type: typeof constants.LOAD_ZONES,
    data: $ReadOnlyArray<Zone>
|};

type SetZoneAction = {|
    type: typeof constants.SET_ZONE,
    data: Zone
|};

type UnsetZoneAction = {|
    type: typeof constants.UNSET_ZONE,
    data: Zone
|};

type CreateZoneAction = {|
    type: typeof constants.CREATE_ZONE,
    [CALL_API]: {
        method: 'POST',
        endpoint: '/api/zone',
        data: ZoneData
    }
|};

type UpdateZoneAction = {|
    type: typeof constants.UPDATE_ZONE,
    [CALL_API]: {
        method: 'PUT',
        endpoint: '/api/zone',
        data: ZoneData
    }
|};

type DeleteZoneAction = {|
    type: typeof constants.DELETE_ZONE,
    [CALL_API]: {
        method: 'DELETE',
        endpoint: string
    }
|};

type ActivateZoneAction = {|
    type: typeof constants.ACTIVATE,
    [CALL_API]: {
        method: 'PUT',
        endpoint: string,
        data: {
            zoneIds: $ReadOnlyArray<strin>,
            duration?: number
        }
    },
    data: {
        zoneIds: $ReadOnlyArray<strin>,
        duration?: number
    }
|};

type SetDrawingShapeTypeAction = {|
    type: typeof constants.SET_DRAWING_SHAPE_TYPE,
    data: null | ShapeType
|};

type SetDrawingShapeAction = {|
    type: typeof constants.SET_DRAWING_SHAPE,
    data: Shape
|};

type SetDrawingZoneAction = {|
    type: typeof constants.SET_DRAWING_ZONE,
    data: null | Zone
|};

type DeactivateZoneAction = {|
    type: typeof constants.DEACTIVATE,
    [CALL_API]: {
        method: 'DELETE',
        endpoint: string,
        data: {
            zoneIds: $ReadOnlyArray<strin>,
        }
    },
    data: {
        zoneIds: $ReadOnlyArray<strin>,
    }
|};

export type ZoneAction =
    | LoadZonesAction
    | SetZoneAction
    | UnsetZoneAction
    | CreateZoneAction
    | UpdateZoneAction
    | DeleteZoneAction
    | ActivateZoneAction
    | DeactivateZoneAction
    | SetDrawingShapeTypeAction
    | SetDrawingShapeAction
    | SetDrawingZoneAction;

const zoneActions = {
    loadZones: (zones: $ReadOnlyArray<Zone>): LoadZonesAction => ({
        type: constants.LOAD_ZONES,
        data: zones
    }),

    setZone: (zone: Zone): SetZoneAction => ({
        type: constants.SET_ZONE,
        data: zone
    }),

    unsetZone: (zone: Zone): UnsetZoneAction => ({
        type: constants.UNSET_ZONE,
        data: zone
    }),

    createZone: (zone: ZoneData): CreateZoneAction => ({
        type: constants.CREATE_ZONE,
        [CALL_API]: {
            method: 'POST',
            endpoint: '/api/zone',
            data: zone
        }
    }),

    updateZone: (zone: ZoneData): UpdateZoneAction => ({
        type: constants.CREATE_ZONE,
        [CALL_API]: {
            method: 'PUT',
            endpoint: '/api/zone',
            data: zone
        }
    }),

    deleteZone: (zoneId: string): DeleteZoneAction => ({
        type: constants.DELETE_ZONE,
        [CALL_API]: {
            method: 'DELETE',
            endpoint: `/api/zone/${zoneId}`
        }
    }),

    activate: (zoneIds: $ReadOnlyArray<string>, duration?: number): ActivateZoneAction => ({
        type: constants.ACTIVATE,
        [CALL_API]: {
            method: 'PUT',
            endpoint: `/api/zone/activate`,
            data: {
                zoneIds,
                duration
            }
        },
        data: {
            zoneIds,
            duration
        }
    }),

    deactivate: (zoneIds: $ReadOnlyArray<string>): DeactivateZoneAction => ({
        type: constants.DEACTIVATE,
        [CALL_API]: {
            method: 'PUT',
            endpoint: `/api/zone/deactivate`,
            data: {
                zoneIds
            }
        },
        data: {
            zoneIds
        }
    }),


    setDrawingShapeType: (type: null | ShapeType): SetDrawingShapeTypeAction => ({
        type: constants.SET_DRAWING_SHAPE_TYPE,
        data: type
    }),

    setDrawingShape: (shape: Shape): SetDrawingShapeAction => ({
        type: constants.SET_DRAWING_SHAPE,
        data: shape
    }),

    setDrawingZone: (zone: null | Zone): SetDrawingZoneAction => ({
        type: constants.SET_DRAWING_ZONE,
        data: zone
    }),

    enableDrawingThunk: (type?: ShapeType, shape?: Shape, zone?: Zone) => (dispatch: Dispatch, getState: GetState) => {
        const state = getState();
        const currentType = drawingShapeTypeSelector(state);
        const currentZone = drawingZoneSelector(state);

        mapService.deselectAll();

        if(typeof type === 'undefined' || type !== currentType) {
            dispatch(zoneActions.setDrawingShapeType(type || SHAPES.polygon));
        }

        // Set shape only if type is defined
        if(type && shape) {
            dispatch(zoneActions.setDrawingShape(shape));
        }

        if(zone !== currentZone) {
            dispatch(zoneActions.setDrawingZone(zone || null));
        }
    },

    disableDrawingThunk: () => zoneActions.setDrawingShapeType(null)
};

export default zoneActions;